import { Identifiable, List } from "@planzone/dto";

function shiftDate(date: Date, shift: number) {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate() + shift,
  ).toISOString();
}

function shiftElement<
  Element extends {
    startDate?: string;
    endDate?: string;
    startTime?: string;
    endTime?: string;
  },
>(element: Element, shift: number): Element {
  const isEvent = "startTime" in element || "endTime" in element;
  const endDate = element.endDate ? new Date(element.endDate) : new Date();
  const startDate = element.startDate
    ? new Date(element.startDate)
    : new Date();
  const endTime = element.endTime ? new Date(element.endTime) : new Date();
  const startTime = element.startTime
    ? new Date(element.startTime)
    : new Date();

  return !isEvent
    ? {
        ...element,
        endDate: element.endDate ? shiftDate(endDate, shift) : element.endDate,
        startDate: element.startDate
          ? shiftDate(startDate, shift)
          : element.startDate,
      }
    : {
        ...element,
        endTime: element.endTime ? shiftDate(endTime, shift) : element.endTime,
        startTime: element.startTime
          ? shiftDate(startTime, shift)
          : element.startTime,
      };
}

function shiftList(
  list: Identifiable<List>,
  shift: number,
  depth = 0,
): Identifiable<List> {
  return depth === 0
    ? {
        ...list,
        tasks: list.tasks.map((task) => shiftElement(task, shift)),
        milestones: list.milestones.map((milestone) =>
          shiftElement(milestone, shift),
        ),
        events: list.events.map((event) => shiftElement(event, shift)),
        subLists: list.subLists.map((subList) =>
          shiftList(subList, shift, depth + 1),
        ),
      }
    : shiftElement(
        {
          ...list,
          tasks: list.tasks.map((task) => shiftElement(task, shift)),
          milestones: list.milestones.map((milestone) =>
            shiftElement(milestone, shift),
          ),
          events: list.events.map((event) => shiftElement(event, shift)),
          subLists: list.subLists.map((subList) =>
            shiftList(subList, shift, depth + 1),
          ),
        },
        shift,
      );
}

function getShift(before: Identifiable<List>, after: Identifiable<List>) {
  return Math.round(
    (new Date(after.endDate).getTime() - new Date(before.endDate).getTime()) /
      (1000 * 60 * 60 * 24),
  );
}

function getStartShift(before: Identifiable<List>, after: Identifiable<List>) {
  return Math.round(
    (new Date(after.startDate).getTime() -
      new Date(before.startDate).getTime()) /
      (1000 * 60 * 60 * 24),
  );
}

function hasShifted(before: Identifiable<List>, after: Identifiable<List>) {
  return (
    !!after.startDate &&
    !!after.endDate &&
    getStartShift(before, after) !== 0 &&
    getStartShift(before, after) === getShift(before, after)
  );
}

export { shiftList, hasShifted, getShift, shiftElement };
